import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  ColorArea,
  ColorField,
  ColorPicker as AriaColorPicker,
  ColorSlider,
  ColorSwatch,
  ColorThumb,
  Dialog,
  DialogTrigger,
  getColorChannels,
  Input,
  parseColor,
  Popover,
  SliderTrack,
  type Color,
  type ColorSpace,
} from 'react-aria-components';
import './ColorPicker.css';

interface ColorPickerProps {
  /** Current color value (hex string, e.g. "#ff0000") */
  value: string;
  /** Called when the color changes */
  onChange: (color: string) => void;
  /** Whether to show the hex value next to the swatch */
  showValue?: boolean;
  /** Retained for backwards-compatible call sites. */
  presetColors?: string[];
  /** Retained for backwards-compatible call sites. */
  pickerWidth?: string;
  /** Custom className for the wrapper */
  className?: string;
}

const FALLBACK_COLOR = '#ffffff';
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const COLOR_CHANGE_DELAY_MS = 150;
type ColorFormat = ColorSpace | 'hex';

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  showValue = false,
  className,
}) => {
  const colorValue = HEX_COLOR_PATTERN.test(value) ? value : FALLBACK_COLOR;
  const [draftColor, setDraftColor] = useState<Color>(() => parseColor(colorValue));
  const [format, setFormat] = useState<ColorFormat>('hex');
  const pendingColorRef = useRef<string | null>(null);
  const commitTimerRef = useRef<number | null>(null);

  const commitPendingColor = useCallback((): void => {
    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }

    const pendingColor = pendingColorRef.current;
    pendingColorRef.current = null;
    if (pendingColor !== null && pendingColor !== value) {
      onChange(pendingColor);
    }
  }, [onChange, value]);

  useEffect(() => {
    setDraftColor(parseColor(colorValue));
  }, [colorValue]);

  useEffect(() => () => {
    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current);
    }
  }, []);

  const handleColorChange = (nextColor: Color): void => {
    const nextHexColor = nextColor.toString('hex').toLowerCase();
    setDraftColor(nextColor);
    pendingColorRef.current = nextHexColor;

    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current);
    }
    commitTimerRef.current = window.setTimeout(commitPendingColor, COLOR_CHANGE_DELAY_MS);
  };

  const wrapperClass = showValue
    ? `color-picker color-picker-with-value ${className ?? ''}`
    : `color-picker ${className ?? ''}`;

  return (
    <div className={wrapperClass.trim()}>
      <AriaColorPicker value={draftColor} onChange={handleColorChange}>
        {({ color }) => {
          const hue = color.toFormat('hsb').getChannelValue('hue');

          return (
            <DialogTrigger>
          <Button className="color-picker-swatch" aria-label="Pick color">
            <ColorSwatch color={draftColor} />
          </Button>
          <Popover className="color-picker-popover" placement="bottom start" offset={8}>
            <Dialog className="color-picker-panel" aria-label="Color picker">
              <ColorArea
                className="color-picker-area"
                colorSpace="hsb"
                xChannel="saturation"
                yChannel="brightness"
                style={{
                  background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hue} 100% 50%))`,
                }}
              >
                <ColorThumb className="color-picker-thumb" />
              </ColorArea>
              <ColorSlider className="color-picker-slider" colorSpace="hsb" channel="hue">
                <SliderTrack
                  className="color-picker-slider-track"
                  style={{
                    background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
                  }}
                >
                  <ColorThumb className="color-picker-thumb color-picker-slider-thumb" />
                </SliderTrack>
              </ColorSlider>
              <div className="color-picker-fields-header">
                <label>
                  <span className="color-picker-format-label">Format</span>
                  <select
                    aria-label="Color format"
                    value={format}
                    onChange={(event) => setFormat(event.currentTarget.value as ColorFormat)}
                  >
                    <option value="hex">Hex</option>
                    <option value="rgb">RGB</option>
                    <option value="hsl">HSL</option>
                    <option value="hsb">HSB</option>
                  </select>
                </label>
              </div>
              {format === 'hex' ? (
                <ColorField className="color-picker-hex-field" aria-label="Hex color">
                  <Input />
                </ColorField>
              ) : (
                <div className="color-picker-channel-fields">
                  {getColorChannels(format).map((channel) => (
                    <ColorField
                      key={channel}
                      className="color-picker-channel-field"
                      colorSpace={format}
                      channel={channel}
                      aria-label={channel}
                    >
                      <Input />
                    </ColorField>
                  ))}
                </div>
              )}
            </Dialog>
          </Popover>
            </DialogTrigger>
          );
        }}
      </AriaColorPicker>

      {showValue && <span className="color-picker-value">{draftColor.toString('hex')}</span>}
    </div>
  );
};

export default ColorPicker;

